import { useEffect } from 'react'

/**
 * F3.UX-9: Global keyboard shortcut handler.
 *
 * Usage:
 *   useKeyboardShortcuts([
 *     { key: 'k', meta: true, handler: () => openSearch() },
 *     { key: 'Escape', handler: () => closeModal() },
 *   ])
 */
export interface Shortcut {
  /** Key to match (case-insensitive) */
  key: string
  /** Whether Ctrl/Cmd is required */
  meta?: boolean
  /** Whether Shift is required */
  shift?: boolean
  /** Whether Alt is required */
  alt?: boolean
  /** Called when matched. Return `true` to skip the default browser action. */
  handler: (e: KeyboardEvent) => void | boolean
  /** Optional: prevent firing when user is typing in an input/textarea */
  ignoreInputs?: boolean
}

export function useKeyboardShortcuts(shortcuts: Shortcut[]) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Skip if user is typing in a text input (unless ignored)
      const target = e.target as HTMLElement
      const inEditable =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable

      for (const sc of shortcuts) {
        const eventKey = e.key || ''
        const shortcutKey = sc.key || ''
        const keyMatches = eventKey.toLowerCase() === shortcutKey.toLowerCase()
        if (!keyMatches) continue
        if (sc.meta && !(e.metaKey || e.ctrlKey)) continue
        if (sc.shift && !e.shiftKey) continue
        if (sc.alt && !e.altKey) continue
        if (!sc.ignoreInputs && inEditable) continue
        if (e.metaKey || e.ctrlKey || e.altKey) {
          if (!sc.meta && !sc.alt) continue
        }
        const result = sc.handler(e)
        if (result === true || result === undefined) {
          e.preventDefault()
        }
        break
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [shortcuts])
}
